import type { SupplierFormIngredientSupplier, SupplierFormType } from "./SupplierForm/SupplierForm.types";

export type PersistedSupplierLink = {
    iri: IRI;
    managedBy?: IRI;
};

/** Persisted links of the supplier, keyed by ingredient IRI. */
export type SupplierLinksBaseline = Map<IRI, PersistedSupplierLink>;

export type SupplierLinksSyncState = {
    organizationId?: number;
    baseline: SupplierLinksBaseline;
};

export type SupplierLinksDiff = {
    toCreate: IRI[];
    toDelete: Array<{ ingredient: IRI; linkIri: IRI }>;
};

export const makeLinksBaseline = (rows: SupplierFormIngredientSupplier[]): SupplierLinksBaseline =>
    new Map(
        rows.flatMap((row) =>
            row["@id"] === undefined ? [] : [[row.ingredient["@id"], { iri: row["@id"], managedBy: row.managedBy }]],
        ),
    );

export const diffSupplierLinks = (
    baseline: SupplierLinksBaseline,
    desired: SupplierFormIngredientSupplier[],
    selectedCompanyIri: IRI,
): SupplierLinksDiff => {
    const desiredIngredients = new Set(desired.map((row) => row.ingredient["@id"]));

    return {
        toCreate: [...desiredIngredients].filter((ingredient) => !baseline.has(ingredient)),
        toDelete: [...baseline.entries()]
            .filter(
                ([ingredient, link]) => !desiredIngredients.has(ingredient) && link.managedBy === selectedCompanyIri,
            )
            .map(([ingredient, link]) => ({ ingredient, linkIri: link.iri })),
    };
};

export const isLinkRemovableBy = (row: SupplierFormIngredientSupplier, selectedCompanyIri: IRI): boolean =>
    row["@id"] === undefined || row.managedBy === selectedCompanyIri;

type ApiResponse = { data?: unknown };

type SaveSupplierWithLinksOptions = {
    draft: SupplierFormType;
    selectedCompanyIri: IRI;
    state: SupplierLinksSyncState;
    saveOrganization: (draft: SupplierFormType) => Promise<ApiResponse>;
    createLink: (params: { ingredient: IRI; supplier: IRI }) => Promise<ApiResponse>;
    deleteLink: (params: { linkIri: IRI }) => Promise<unknown>;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * Saves the organization first, then removes and adds the links one by one.
 * The state survives a failure so that a retry neither re-creates the organization nor repeats a done call.
 */
export const saveSupplierWithLinks = async ({
    draft,
    selectedCompanyIri,
    state,
    saveOrganization,
    createLink,
    deleteLink,
}: SaveSupplierWithLinksOptions): Promise<{ data: Record<string, unknown> }> => {
    const organizationResponse = await saveOrganization({ ...draft, id: draft.id ?? state.organizationId });
    const organization = isRecord(organizationResponse.data) ? organizationResponse.data : {};
    const organizationId = typeof organization.id === "number" ? organization.id : undefined;
    if (organizationId !== undefined) {
        state.organizationId = organizationId;
    }
    const supplierIri =
        typeof organization["@id"] === "string" ? (organization["@id"] as IRI)
        : organizationId !== undefined ? (`/api/organizations/${organizationId}` as IRI)
        : undefined;

    const diff = diffSupplierLinks(state.baseline, draft.ingredientSuppliers, selectedCompanyIri);

    for (const { ingredient, linkIri } of diff.toDelete) {
        await deleteLink({ linkIri });
        state.baseline.delete(ingredient);
    }

    for (const ingredient of diff.toCreate) {
        if (supplierIri === undefined) {
            throw new Error("The saved organization does not expose its identity.");
        }
        const response = await createLink({ ingredient, supplier: supplierIri });
        const linkIri = isRecord(response.data) ? response.data["@id"] : undefined;
        if (typeof linkIri !== "string") {
            throw new Error("The created ingredient-supplier link does not expose its identity.");
        }
        state.baseline.set(ingredient, { iri: linkIri as IRI, managedBy: selectedCompanyIri });
    }

    return {
        data: {
            ...organization,
            ingredientSuppliers: [...state.baseline.entries()].map(([ingredient, link]) => ({
                "@id": link.iri,
                ingredient,
            })),
        },
    };
};
